/**
 * evidenceSourceDiff — Citation_Traceability_Architecture CT11.
 *
 * Per-rerun source diff: surfaces which evidence atoms changed since
 * the prior report version so the user can see WHY a verdict shifted
 * in evidence terms, not just narrative terms.
 *
 * Five categories (matching the pack-spec copy in
 * `Citation_Traceability_Architecture_Prompt.md` §CT11):
 *
 *   + Added amendments
 *   + Added documents (newly cited)
 *   ~ Changed provider values (provider key present in both, value
 *     differs OR capturedAt advanced)
 *   − Removed documents (disconnected links)
 *   − Removed Site Context claims (claim no longer present in the
 *     latest snapshot — e.g. user deleted or re-extracted)
 *
 * Pure function — no React, no store reads, no Firestore.
 *
 * Composition: callers feed two "evidence snapshots". The shape
 * lives next to the diff to keep it small and serialisable; the
 * caller projects whatever scattered fields it wants into the
 * snapshot at the boundary.
 */

/* ── Public shapes ───────────────────────────────────────────────────── */

/** A snapshot of the evidence atoms that informed one report. */
export interface EvidenceSnapshot {
  /** Approved-amendment ids that were live at gen time. */
  amendmentIds: string[];
  /** Document ids included in the report's documentRefs (user-included only). */
  documentIds: string[];
  /** Provider responses keyed by provider key. The value MUST be
   *  JSON-stringifiable so the diff can detect changes deterministically. */
  providers: Record<string, { value: unknown; capturedAt?: string }>;
  /** Site-Context claim ids included in the report's snapshot. */
  siteContextClaimIds: string[];
}

export type EvidenceDiffKind =
  | 'amendment-added'
  | 'amendment-removed'
  | 'document-added'
  | 'document-removed'
  | 'provider-changed'
  | 'provider-added'
  | 'provider-removed'
  | 'site-context-claim-added'
  | 'site-context-claim-removed';

export interface EvidenceDiffEntry {
  kind: EvidenceDiffKind;
  /** Identity of the changed atom — amendment id / doc id / provider
   *  key / claim id. */
  id: string;
  /** Human-readable label for the chip — same caption surfaces in the
   *  rerun diff sidebar and in the report-version compare view. */
  label: string;
}

export interface EvidenceSourceDiff {
  added: EvidenceDiffEntry[];
  removed: EvidenceDiffEntry[];
  changed: EvidenceDiffEntry[];
  totalChanges: number;
  /** Mirrors the pack-spec's reproducibility-guarantee copy:
   *  "this rerun is reproducible from these N source changes." */
  reproducibleFrom: number;
}

/* ── Snapshot construction ──────────────────────────────────────────── */

/**
 * Empty snapshot — used as the baseline when a v1 (no prior version)
 * is being diffed.
 */
export function emptyEvidenceSnapshot(): EvidenceSnapshot {
  return {
    amendmentIds: [],
    documentIds: [],
    providers: {},
    siteContextClaimIds: [],
  };
}

/* ── Diff ───────────────────────────────────────────────────────────── */

/**
 * Compute the source-level diff between two snapshots. `from` is the
 * prior version; `to` is the new version. Order of returned entries:
 *
 *   - added[]   : amendments → docs → providers → claims (within
 *                 each group, sorted alphabetically by id for stable
 *                 rendering).
 *   - removed[] : same order.
 *   - changed[] : providers only today (amendments + claims aren't
 *                 in-place mutable; if their id is the same, their
 *                 content is the same).
 *
 * `reproducibleFrom` equals `totalChanges` — the rerun is
 * reproducible from those changes alone. Naming surfaces the
 * reproducibility guarantee at the call site.
 */
export function computeEvidenceSourceDiff(
  from: EvidenceSnapshot,
  to: EvidenceSnapshot,
): EvidenceSourceDiff {
  const added: EvidenceDiffEntry[] = [];
  const removed: EvidenceDiffEntry[] = [];
  const changed: EvidenceDiffEntry[] = [];

  /* Amendments. */
  const fromAmds = new Set(from.amendmentIds);
  const toAmds = new Set(to.amendmentIds);
  for (const id of sortedDiff(toAmds, fromAmds)) {
    added.push({ kind: 'amendment-added', id, label: `Amendment ${id}` });
  }
  for (const id of sortedDiff(fromAmds, toAmds)) {
    removed.push({ kind: 'amendment-removed', id, label: `Amendment ${id}` });
  }

  /* Documents. */
  const fromDocs = new Set(from.documentIds);
  const toDocs = new Set(to.documentIds);
  for (const id of sortedDiff(toDocs, fromDocs)) {
    added.push({ kind: 'document-added', id, label: `Document ${id}` });
  }
  for (const id of sortedDiff(fromDocs, toDocs)) {
    removed.push({ kind: 'document-removed', id, label: `Document ${id}` });
  }

  /* Providers — added / removed / changed. */
  const fromProv = from.providers ?? {};
  const toProv = to.providers ?? {};
  const fromKeys = Object.keys(fromProv).sort();
  const toKeys = Object.keys(toProv).sort();
  const fromKeySet = new Set(fromKeys);
  const toKeySet = new Set(toKeys);
  for (const key of toKeys) {
    if (!fromKeySet.has(key)) {
      added.push({ kind: 'provider-added', id: key, label: `Provider ${key}` });
      continue;
    }
    const a = fromProv[key];
    const b = toProv[key];
    if (!providerEqual(a, b)) {
      changed.push({ kind: 'provider-changed', id: key, label: `Provider ${key}` });
    }
  }
  for (const key of fromKeys) {
    if (!toKeySet.has(key)) {
      removed.push({ kind: 'provider-removed', id: key, label: `Provider ${key}` });
    }
  }

  /* Site-Context claims. */
  const fromClaims = new Set(from.siteContextClaimIds);
  const toClaims = new Set(to.siteContextClaimIds);
  for (const id of sortedDiff(toClaims, fromClaims)) {
    added.push({ kind: 'site-context-claim-added', id, label: `Site Context claim ${id}` });
  }
  for (const id of sortedDiff(fromClaims, toClaims)) {
    removed.push({ kind: 'site-context-claim-removed', id, label: `Site Context claim ${id}` });
  }

  const totalChanges = added.length + removed.length + changed.length;
  return { added, removed, changed, totalChanges, reproducibleFrom: totalChanges };
}

/* ── Caption helpers ────────────────────────────────────────────────── */

/**
 * Format the inline chip caption — mirrors the pack-spec line:
 * `+1 amendment / +1 doc / ~1 provider value / −1 disconnected link`.
 *
 * Skips zero-count categories.
 */
export function formatEvidenceSourceDiffCaption(diff: EvidenceSourceDiff): string {
  const parts: string[] = [];
  const amdAdds = diff.added.filter(e => e.kind === 'amendment-added').length;
  const docAdds = diff.added.filter(e => e.kind === 'document-added').length;
  const docRems = diff.removed.filter(e => e.kind === 'document-removed').length;
  const claimAdds = diff.added.filter(e => e.kind === 'site-context-claim-added').length;
  const claimRems = diff.removed.filter(e => e.kind === 'site-context-claim-removed').length;
  const provAdds = diff.added.filter(e => e.kind === 'provider-added').length;
  const provRems = diff.removed.filter(e => e.kind === 'provider-removed').length;
  const provChgs = diff.changed.length;

  if (amdAdds > 0) parts.push(`+${amdAdds} amendment${amdAdds === 1 ? '' : 's'}`);
  if (docAdds > 0) parts.push(`+${docAdds} doc${docAdds === 1 ? '' : 's'}`);
  if (provChgs > 0) parts.push(`~${provChgs} provider value${provChgs === 1 ? '' : 's'}`);
  if (provAdds > 0) parts.push(`+${provAdds} provider${provAdds === 1 ? '' : 's'}`);
  if (claimAdds > 0) parts.push(`+${claimAdds} claim${claimAdds === 1 ? '' : 's'}`);
  if (docRems > 0)  parts.push(`−${docRems} doc${docRems === 1 ? '' : 's'}`);
  if (provRems > 0) parts.push(`−${provRems} provider${provRems === 1 ? '' : 's'}`);
  if (claimRems > 0) parts.push(`−${claimRems} claim${claimRems === 1 ? '' : 's'}`);

  return parts.join(' · ');
}

/* ── Internals ──────────────────────────────────────────────────────── */

function sortedDiff(a: Set<string>, b: Set<string>): string[] {
  return [...a].filter(x => !b.has(x)).sort();
}

/**
 * Deep equality for provider snapshot entries. We treat capturedAt
 * advance + value change as "changed". Identical capturedAt with
 * identical value is "unchanged" — the diff doesn't fire on a
 * timestamp refresh that didn't move the underlying value, because
 * stage-c's claim-density math only cares about the value.
 */
function providerEqual(
  a: { value: unknown; capturedAt?: string },
  b: { value: unknown; capturedAt?: string },
): boolean {
  if (safeStringify(a.value) !== safeStringify(b.value)) return false;
  // A capturedAt move alone without a value change is treated as
  // unchanged — the rerun-diff caption stays meaningful (a v→v
  // refresh isn't noise).
  return true;
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v); } catch { return String(v); }
}
